/**
 * プリセット作成/編集ページ コンテナ
 */

import type { Preset } from "hooks/use-preset-api";
import {
  type CreatePresetInput,
  useCreatePreset,
  useParsePresetText,
  usePreset,
  useUpdatePreset,
} from "hooks/use-preset-api";
import { useTalentDetail } from "hooks/use-talents-api";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EMPLOYMENT_TYPE_TO_HR_TYPE } from "routes/crm/utils";
import { PresetFormPage } from "routes/settings/PresetFormPage/PresetFormPage";

/**
 * タレント情報をプリセットのinitialData形式に変換する
 * @param {object} talent タレント詳細
 * @return {Preset} プリセット初期データ
 */
function talentToPresetInitialData(talent: {
  name: string;
  skills: { skillName: string }[];
  desiredReward: number | null;
  nearestStation: string | null;
  remotePreference: string | null;
  age: number | null;
  employmentType: string;
  availableFrom: string | null;
}): Partial<Preset> {
  const hrTypes = EMPLOYMENT_TYPE_TO_HR_TYPE[talent.employmentType] ?? null;

  return {
    name: `${talent.name}のプリセット`,
    presetType: "hr",
    skills: talent.skills.map(s => s.skillName),
    reward: talent.desiredReward ? talent.desiredReward * 10000 : null,
    station: talent.nearestStation,
    remoteFrequency: talent.remotePreference,
    availableFrom: talent.availableFrom,
    age: talent.age,
    hrTypes,
    isActive: true,
  } as Partial<Preset>;
}

/**
 * プリセット作成/編集ページのコンテナコンポーネント
 * URLパラメータ ?fromTalentId=xxx でタレントから作成可能
 * @return {JSX.Element} PresetFormPage コンテナ
 */
const PresetFormPageContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const fromTalentId = searchParams.get("fromTalentId");

  const { preset, loading } = usePreset(id);
  const { talent: sourceTalent, loading: talentLoading } = useTalentDetail(
    fromTalentId || undefined,
  );
  const { createPreset, loading: isCreating } = useCreatePreset();
  const { updatePreset, loading: isUpdating } = useUpdatePreset();
  const { parseText, loading: isParsing } = useParsePresetText();

  const [talentInitialData, setTalentInitialData] =
    useState<Partial<Preset> | null>(null);

  useEffect(() => {
    if (fromTalentId && sourceTalent && !talentLoading) {
      setTalentInitialData(
        talentToPresetInitialData({
          ...sourceTalent,
          employmentType: sourceTalent.employmentType ?? "full_time",
        }),
      );
    }
  }, [fromTalentId, sourceTalent, talentLoading]);

  const handleSubmit = useCallback(
    async (input: CreatePresetInput) => {
      if (isEdit && id) {
        await updatePreset(id, input);
      } else {
        await createPreset(input);
      }
      navigate("/settings/presets");
    },
    [isEdit, id, createPreset, updatePreset, navigate],
  );

  const handleParseText = useCallback(
    async (text: string, presetType: string) => {
      return await parseText(text, presetType);
    },
    [parseText],
  );

  const effectiveInitialData = isEdit ? preset : (talentInitialData ?? null);
  const effectiveLoading = isEdit
    ? loading
    : fromTalentId
      ? talentLoading
      : false;

  return (
    <PresetFormPage
      isEdit={isEdit}
      initialData={effectiveInitialData}
      loading={effectiveLoading}
      onSubmit={handleSubmit}
      onParseText={handleParseText}
      isSubmitting={isCreating || isUpdating}
      isParsing={isParsing}
    />
  );
};

export default PresetFormPageContainer;
